import { useMemo, useState } from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import {
  AudioFileList,
  OptionalFile,
  VoiceCreateModalProps,
  VoicePreviewList,
} from "@/types/voice/voice-playground";
import { VoicePreview } from "@/types/voice/voice-creation";
import { useVoiceDesignPreview, useCreateVoiceFromPreview } from "@/hooks/voice/useVoiceDesign";
import { useVoiceRemixPreview } from "@/hooks/voice/useVoiceRemix";
import { useInstantVoiceClone } from "@/hooks/voice/useInstantVoiceClone";
import { useProfessionalVoiceClone } from "@/hooks/voice/useProfessionalVoiceClone";
import { usePvcDraftState } from "@/hooks/voice/usePvcDraftState";
import { useVoiceFavorites } from "@/hooks/voice/useVoiceFavorites";
import { useRecommendedVoices } from "@/hooks/voice/useVoices";

const previewAudioSrc = (preview: VoicePreview) =>
  preview.audio_base_64
    ? `data:${preview.media_type || "audio/mpeg"};base64,${preview.audio_base_64}`
    : "";

export const VoiceCreateModal = ({ isOpen, onClose, onVoiceCreated }: VoiceCreateModalProps) => {
  const [activeTab, setActiveTab] = useState("design");

  // Voice design state
  const [designDescription, setDesignDescription] = useState("");
  const [designText, setDesignText] = useState("");
  const [designName, setDesignName] = useState("");
  const [designPreviews, setDesignPreviews] = useState<VoicePreviewList>([]);
  const [selectedDesignPreview, setSelectedDesignPreview] = useState<string>("");

  // Remix state
  const [remixVoiceId, setRemixVoiceId] = useState("");
  const [remixDescription, setRemixDescription] = useState("");
  const [remixText, setRemixText] = useState("");
  const [remixPreviews, setRemixPreviews] = useState<VoicePreviewList>([]);
  const [selectedRemixPreview, setSelectedRemixPreview] = useState<string>("");
  const [remixName, setRemixName] = useState("");

  // Instant clone state
  const [cloneName, setCloneName] = useState("");
  const [cloneDescription, setCloneDescription] = useState("");
  const [cloneFiles, setCloneFiles] = useState<AudioFileList>([]);
  const [cloneNoiseRemoval, setCloneNoiseRemoval] = useState(true);

  // PVC state
  const [pvcName, setPvcName] = useState("");
  const [pvcLanguage, setPvcLanguage] = useState("en");
  const [pvcDescription, setPvcDescription] = useState("");
  const [pvcFiles, setPvcFiles] = useState<AudioFileList>([]);
  const [pvcSampleId, setPvcSampleId] = useState("");
  const [pvcSpeakerId, setPvcSpeakerId] = useState("");
  const [pvcModelId, setPvcModelId] = useState("eleven_multilingual_v2");
  const [pvcVerificationRecording, setPvcVerificationRecording] = useState<OptionalFile>(null);

  const { draft, updateDraft, clearDraft } = usePvcDraftState();

  // Fetch user's favorite voices for the remix dropdown
  const { data: favoriteVoices = [] } = useVoiceFavorites();
  const { data: recommendedData } = useRecommendedVoices();

  // Categories that indicate a voice can be remixed (user-created voices only)
  // Library voices (premade, professional from library, high_quality, etc.) cannot be remixed
  const REMIXABLE_CATEGORIES = ["cloned", "generated", "ivc", "pvc"];

  const isRemixableVoice = (category?: string) => {
    if (!category) return false;
    const lowerCategory = category.toLowerCase();
    return REMIXABLE_CATEGORIES.some((c) => lowerCategory.includes(c));
  };

  // Only show voices that can actually be remixed (user-created voices)
  const availableVoicesForRemix = useMemo(() => {
    const voiceMap = new Map<string, { id: string; name: string; category?: string }>();

    // Check favorites for remixable voices
    favoriteVoices.forEach((fav) => {
      if (fav.voice_data && fav.voice_id && isRemixableVoice(fav.voice_data.category)) {
        voiceMap.set(fav.voice_id, {
          id: fav.voice_id,
          name: fav.voice_data.name || "Unknown Voice",
          category: fav.voice_data.category,
        });
      }
    });

    // Check recommended voices for remixable voices
    if (recommendedData && Array.isArray(recommendedData)) {
      recommendedData.forEach((voice) => {
        if (voice.id && !voiceMap.has(voice.id) && isRemixableVoice(voice.category)) {
          voiceMap.set(voice.id, {
            id: voice.id,
            name: voice.name || "Unknown Voice",
            category: voice.category,
          });
        }
      });
    }

    return Array.from(voiceMap.values());
  }, [favoriteVoices, recommendedData]);

  const designPreviewMutation = useVoiceDesignPreview();
  const createFromPreviewMutation = useCreateVoiceFromPreview();
  const remixPreviewMutation = useVoiceRemixPreview();
  const instantCloneMutation = useInstantVoiceClone();
  const pvc = useProfessionalVoiceClone();

  const hasDraft = useMemo(() => !!draft.voiceId, [draft.voiceId]);

  const handleDesignPreview = () => {
    if (!designDescription.trim()) {
      toast.error("Add a voice description.");
      return;
    }
    designPreviewMutation.mutate(
      {
        voice_description: designDescription.trim(),
        text: designText.trim() || null,
        auto_generate_text: !designText.trim(),
      },
      {
        onSuccess: (data) => {
          setDesignPreviews(data.previews || []);
          setSelectedDesignPreview(data.previews?.[0]?.generated_voice_id || "");
        },
      }
    );
  };

  const handleCreateFromPreview = (previewId: string, description: string, name: string) => {
    if (!previewId || !name.trim()) {
      toast.error("Select a preview and provide a voice name.");
      return;
    }
    createFromPreviewMutation.mutate(
      {
        generated_voice_id: previewId,
        voice_description: description.trim(),
        voice_name: name.trim(),
      },
      {
        onSuccess: () => {
          toast.success("Voice created successfully! Check the 'Recommended' tab to find it.");
          setDesignPreviews([]);
          setDesignDescription("");
          setDesignText("");
          setDesignName("");
          setSelectedDesignPreview("");
          onVoiceCreated?.();
          onClose();
        },
      }
    );
  };

  const handleRemixPreview = () => {
    if (!remixVoiceId.trim() || !remixDescription.trim()) {
      toast.error("Select a voice and describe how it should change.");
      return;
    }
    // If user provided text, it must be at least 100 characters (ElevenLabs requirement)
    if (remixText.trim() && remixText.trim().length < 100) {
      toast.error("Sample text must be at least 100 characters, or leave it blank to auto-generate.");
      return;
    }
    remixPreviewMutation.mutate(
      {
        voice_id: remixVoiceId.trim(),
        voice_description: remixDescription.trim(),
        text: remixText.trim() || null,
        auto_generate_text: !remixText.trim(),
      },
      {
        onSuccess: (data) => {
          setRemixPreviews(data.previews || []);
          setSelectedRemixPreview(data.previews?.[0]?.generated_voice_id || "");
        },
      }
    );
  };

  const handleInstantClone = () => {
    if (!cloneName.trim() || cloneFiles.length === 0) {
      toast.error("Provide a name and at least one sample.");
      return;
    }
    instantCloneMutation.mutate(
      {
        name: cloneName.trim(),
        files: cloneFiles,
        remove_background_noise: cloneNoiseRemoval,
        description: cloneDescription.trim() ? cloneDescription.trim() : null,
      },
      {
        onSuccess: (data) => {
          toast.success("Instant voice clone created! Check the 'Recommended' tab to find it.");
          if (data?.voice_id) {
            updateDraft({ ...draft, voiceId: data.voice_id, voiceName: cloneName.trim() });
          }
          setCloneName("");
          setCloneDescription("");
          setCloneFiles([]);
          onVoiceCreated?.();
          onClose();
        },
      }
    );
  };

  const handleCreatePvc = () => {
    if (!pvcName.trim() || !pvcLanguage.trim()) {
      toast.error("Provide a PVC name and language.");
      return;
    }
    pvc.create.mutate(
      {
        name: pvcName.trim(),
        language: pvcLanguage.trim(),
        description: pvcDescription.trim() ? pvcDescription.trim() : null,
      },
      {
        onSuccess: (data) => {
          toast.success("PVC created.");
          updateDraft({ ...draft, voiceId: data.voice_id, voiceName: pvcName.trim(), language: pvcLanguage.trim() });
        },
      }
    );
  };

  const handleUploadPvcSamples = () => {
    if (!draft.voiceId || pvcFiles.length === 0) {
      toast.error("Select samples and ensure PVC voice ID is set.");
      return;
    }
    pvc.uploadSamples.mutate(
      {
        voice_id: draft.voiceId,
        files: pvcFiles,
      },
      {
        onSuccess: (data) => {
          const sampleIds = data.map((sample) => sample.sample_id);
          updateDraft({ ...draft, sampleIds });
          if (sampleIds.length > 0) {
            setPvcSampleId(sampleIds[0]);
          }
          toast.success("PVC samples uploaded.");
        },
      }
    );
  };

  const handleSeparateSpeakers = () => {
    if (!draft.voiceId || !pvcSampleId.trim()) {
      toast.error("Provide a PVC voice ID and sample ID.");
      return;
    }
    pvc.separateSpeakers.mutate({ voiceId: draft.voiceId, sampleId: pvcSampleId.trim() }, { onSuccess: () => toast.success("Speaker separation started.") });
  };

  const handleSpeakerStatus = () => {
    if (!draft.voiceId || !pvcSampleId.trim()) {
      toast.error("Provide a PVC voice ID and sample ID.");
      return;
    }
    pvc.speakerStatus.mutate({ voiceId: draft.voiceId, sampleId: pvcSampleId.trim() });
  };

  const handleSelectSpeaker = () => {
    if (!draft.voiceId || !pvcSampleId.trim() || !pvcSpeakerId.trim()) {
      toast.error("Provide voice ID, sample ID, and speaker ID.");
      return;
    }
    pvc.updateSample.mutate({
      voice_id: draft.voiceId,
      sample_id: pvcSampleId.trim(),
      selected_speaker_ids: [pvcSpeakerId.trim()],
    });
  };

  const handleCaptcha = () => {
    if (!draft.voiceId) {
      toast.error("Provide a PVC voice ID.");
      return;
    }
    pvc.getCaptcha.mutate(draft.voiceId);
  };

  const handleVerifyCaptcha = () => {
    if (!draft.voiceId || !pvcVerificationRecording) {
      toast.error("Provide a voice ID and verification recording.");
      return;
    }
    pvc.verifyCaptcha.mutate({ voiceId: draft.voiceId, recording: pvcVerificationRecording });
  };

  const handleTrainPvc = () => {
    if (!draft.voiceId) {
      toast.error("Provide a PVC voice ID.");
      return;
    }
    pvc.train.mutate({ voice_id: draft.voiceId, model_id: pvcModelId });
  };

  const handlePvcStatus = () => {
    if (!draft.voiceId) {
      toast.error("Provide a PVC voice ID.");
      return;
    }
    pvc.status.mutate(draft.voiceId);
  };

  const speakerStatusData = pvc.speakerStatus.data;

  const primaryActionConfig = useMemo(() => {
    if (activeTab === "design") {
      const canCreate = !!selectedDesignPreview && !!designName.trim() && designPreviews.length > 0;
      return {
        label: canCreate ? "Create voice" : "Generate previews",
        disabled: designPreviewMutation.isPending || createFromPreviewMutation.isPending || (!designDescription.trim() && !canCreate),
        onClick: () =>
          canCreate
            ? handleCreateFromPreview(selectedDesignPreview, designDescription, designName)
            : handleDesignPreview(),
        isBusy: designPreviewMutation.isPending || createFromPreviewMutation.isPending,
      };
    }

    if (activeTab === "remix") {
      const canCreate = !!selectedRemixPreview && !!remixName.trim() && remixPreviews.length > 0;
      return {
        label: canCreate ? "Create remix voice" : "Generate remix previews",
        disabled: remixPreviewMutation.isPending || createFromPreviewMutation.isPending || (!remixDescription.trim() && !canCreate),
        onClick: () =>
          canCreate
            ? handleCreateFromPreview(selectedRemixPreview, remixDescription, remixName)
            : handleRemixPreview(),
        isBusy: remixPreviewMutation.isPending || createFromPreviewMutation.isPending,
      };
    }

    if (activeTab === "instant") {
      return {
        label: "Create instant clone",
        disabled: instantCloneMutation.isPending || !cloneName.trim() || cloneFiles.length === 0,
        onClick: handleInstantClone,
        isBusy: instantCloneMutation.isPending,
      };
    }

    if (!draft.voiceId) {
      return {
        label: "Create PVC voice",
        disabled: pvc.create.isPending || !pvcName.trim() || !pvcLanguage.trim(),
        onClick: handleCreatePvc,
        isBusy: pvc.create.isPending,
      };
    }

    if (pvcFiles.length > 0) {
      return {
        label: "Upload samples",
        disabled: pvc.uploadSamples.isPending,
        onClick: handleUploadPvcSamples,
        isBusy: pvc.uploadSamples.isPending,
      };
    }

    return {
      label: "Check status",
      disabled: pvc.status.isPending,
      onClick: handlePvcStatus,
      isBusy: pvc.status.isPending,
    };
  }, [
    activeTab,
    cloneFiles.length,
    cloneName,
    createFromPreviewMutation.isPending,
    designDescription,
    designName,
    designPreviews.length,
    designPreviewMutation.isPending,
    draft.voiceId,
    handleCreatePvc,
    handleDesignPreview,
    handleInstantClone,
    handleRemixPreview,
    handleUploadPvcSamples,
    handlePvcStatus,
    instantCloneMutation.isPending,
    pvc.create.isPending,
    pvc.status.isPending,
    pvc.uploadSamples.isPending,
    pvcFiles.length,
    pvcLanguage,
    pvcName,
    remixDescription,
    remixName,
    remixPreviewMutation.isPending,
    remixPreviews.length,
    selectedDesignPreview,
    selectedRemixPreview,
  ]);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        showClose={false}
        className="w-full sm:max-w-xl md:max-w-2xl lg:max-w-3xl bg-white shadow-2xl overflow-hidden p-0"
      >
        <div className="h-full flex flex-col">
          <div className="bg-gradient-to-r from-genie-primary to-genie-secondary p-4 sm:p-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white">Create a Custom Voice</h2>
                <p className="text-sm text-white/80">Give your genie a unique voice that matches your brand personality.</p>
              </div>
              <button
                onClick={onClose}
                className="rounded-full p-2 text-white/70 hover:text-white hover:bg-white/20 transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-8 pt-2 sm:pt-4 pb-4 sm:pb-8">
            <div className="pb-24">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-3 bg-gray-100/50 p-1 rounded-lg">
                  <TabsTrigger value="design" className="data-[state=active]:bg-genie-primary data-[state=active]:text-white data-[state=active]:shadow-md transition-all">
                    Voice Design
                  </TabsTrigger>
                  <TabsTrigger value="instant" className="data-[state=active]:bg-genie-primary data-[state=active]:text-white data-[state=active]:shadow-md transition-all">
                    Instant Clone
                  </TabsTrigger>
                  <TabsTrigger value="remix" className="data-[state=active]:bg-genie-primary data-[state=active]:text-white data-[state=active]:shadow-md transition-all">
                    Voice Remixing
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="design" className="space-y-4 pt-6">
                  <p className="text-sm text-muted-foreground">
                    Describe your ideal voice and we'll generate it for you. Perfect for creating a unique brand voice when you don't have audio samples to clone.
                  </p>
                  <div className="space-y-2">
                    <Label>Describe your ideal voice</Label>
                    <Textarea
                      value={designDescription}
                      onChange={(event) => setDesignDescription(event.target.value)}
                      placeholder="e.g., A warm, friendly female voice with a slight British accent, professional but approachable - perfect for customer support"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sample text (optional)</Label>
                    <Textarea
                      value={designText}
                      onChange={(event) => setDesignText(event.target.value)}
                      placeholder="Enter text for the voice to speak in the preview. Leave blank and we'll generate something suitable."
                    />
                  </div>

                  {designPreviews.length > 0 && (
                    <div className="space-y-3">
                      <Label>Listen to your options</Label>
                      <p className="text-xs text-muted-foreground">We generated a few variations - pick the one that sounds best for your genie.</p>
                      <div className="grid gap-3 md:grid-cols-2">
                        {designPreviews.map((preview) => (
                          <div key={preview.generated_voice_id} className="border rounded-lg p-3 space-y-2">
                            <audio controls src={previewAudioSrc(preview)} className="w-full" />
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={selectedDesignPreview === preview.generated_voice_id}
                                onCheckedChange={() => setSelectedDesignPreview(preview.generated_voice_id)}
                              />
                              <span className="text-sm">Use this voice</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <Label>Name your voice</Label>
                        <Input value={designName} onChange={(event) => setDesignName(event.target.value)} placeholder="e.g., 'Sophie - Support'" />
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="instant" className="space-y-4 pt-6">
                  <p className="text-sm text-muted-foreground">
                    Clone a real voice from audio recordings. Upload samples of someone speaking and create a voice your genie can use. Great for maintaining brand consistency with an existing spokesperson or team member.
                  </p>
                  <div className="space-y-2">
                    <Label>Name your cloned voice</Label>
                    <Input value={cloneName} onChange={(event) => setCloneName(event.target.value)} placeholder="e.g., 'Sarah - Sales Team'" />
                  </div>
                  <div className="space-y-2">
                    <Label>Voice description (optional)</Label>
                    <Textarea value={cloneDescription} onChange={(event) => setCloneDescription(event.target.value)} placeholder="Add notes about this voice, e.g., 'Our head of customer success'" />
                  </div>
                  <div className="space-y-2">
                    <Label>Upload voice samples</Label>
                    <Input
                      type="file"
                      multiple
                      accept="audio/*"
                      onChange={(event) => setCloneFiles(event.target.files ? Array.from(event.target.files) : [])}
                    />
                    <p className="text-xs text-muted-foreground">
                      Upload 1-25 audio files (MP3, WAV). For best results, use clear recordings with minimal background noise. 30 seconds to 3 minutes total recommended.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={cloneNoiseRemoval} onCheckedChange={(value) => setCloneNoiseRemoval(Boolean(value))} />
                    <span className="text-sm">Clean up audio automatically</span>
                    <span className="text-xs text-muted-foreground">(recommended if recordings have background noise)</span>
                  </div>
                </TabsContent>

                <TabsContent value="pvc" className="space-y-6 pt-6">
                  <div className="space-y-2">
                    <Label>Step 1: Create PVC</Label>
                    <div className="grid gap-3 md:grid-cols-2">
                      <Input value={pvcName} onChange={(event) => setPvcName(event.target.value)} placeholder="PVC name" />
                      <Input value={pvcLanguage} onChange={(event) => setPvcLanguage(event.target.value)} placeholder="Language (e.g. en)" />
                    </div>
                    <Textarea value={pvcDescription} onChange={(event) => setPvcDescription(event.target.value)} placeholder="PVC description (optional)" />
                    {hasDraft && (
                      <div className="text-xs text-gray-500">
                        Current PVC voice ID: <span className="font-mono">{draft.voiceId}</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Step 2: Upload samples</Label>
                    <Input
                      type="file"
                      multiple
                      accept="audio/*"
                      onChange={(event) => setPvcFiles(event.target.files ? Array.from(event.target.files) : [])}
                    />
                    {draft.sampleIds?.length ? (
                      <div className="text-xs text-gray-500">Sample IDs: {draft.sampleIds.join(", ")}</div>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <Label>Step 3: Speaker separation</Label>
                    <Input value={pvcSampleId} onChange={(event) => setPvcSampleId(event.target.value)} placeholder="Sample ID" />
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" onClick={handleSeparateSpeakers} disabled={pvc.separateSpeakers.isPending}>
                        Start separation
                      </Button>
                      <Button variant="outline" onClick={handleSpeakerStatus} disabled={pvc.speakerStatus.isPending}>
                        Check status
                      </Button>
                    </div>
                    {speakerStatusData ? (
                      <div className="text-xs text-gray-500">
                        Status: {speakerStatusData.status} | Speakers: {speakerStatusData.speakers ? Object.keys(speakerStatusData.speakers).length : 0}
                      </div>
                    ) : null}
                    <Input value={pvcSpeakerId} onChange={(event) => setPvcSpeakerId(event.target.value)} placeholder="Speaker ID to use" />
                    <Button variant="outline" onClick={handleSelectSpeaker} disabled={pvc.updateSample.isPending}>
                      Select speaker
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>Step 4: Verification</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" onClick={handleCaptcha} disabled={pvc.getCaptcha.isPending}>
                        {pvc.getCaptcha.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                        Get captcha
                      </Button>
                      {pvc.getCaptcha.data?.data_base64 ? (
                        <img
                          src={`data:${pvc.getCaptcha.data.mime_type};base64,${pvc.getCaptcha.data.data_base64}`}
                          alt="PVC captcha"
                          className="max-h-24 rounded border"
                        />
                      ) : null}
                    </div>
                    <Input
                      type="file"
                      accept="audio/*"
                      onChange={(event) => setPvcVerificationRecording(event.target.files?.[0] || null)}
                    />
                    <Button variant="outline" onClick={handleVerifyCaptcha} disabled={pvc.verifyCaptcha.isPending}>
                      {pvc.verifyCaptcha.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Verify captcha
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>Step 5: Train PVC</Label>
                    <Input value={pvcModelId} onChange={(event) => setPvcModelId(event.target.value)} placeholder="Model ID" />
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={handleTrainPvc} disabled={pvc.train.isPending}>
                        {pvc.train.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                        Start training
                      </Button>
                      <Button variant="outline" onClick={handlePvcStatus} disabled={pvc.status.isPending}>
                        Check status
                      </Button>
                      <Button variant="ghost" onClick={clearDraft}>
                        Clear draft
                      </Button>
                    </div>
                    {pvc.status.data?.fine_tuning?.state ? (
                      <div className="text-xs text-gray-500">
                        Fine-tuning state: {JSON.stringify(pvc.status.data.fine_tuning.state)}
                      </div>
                    ) : null}
                  </div>
                </TabsContent>

                <TabsContent value="remix" className="space-y-4 pt-6">
                  <p className="text-sm text-muted-foreground">
                    Take an existing voice and modify it with new characteristics. Perfect for tweaking a voice you like to better match your brand - make it warmer, more energetic, or adjust the accent.
                  </p>
                  <div className="space-y-2">
                    <Label>Select a voice to remix</Label>
                    {availableVoicesForRemix.length > 0 ? (
                      <>
                        <Select value={remixVoiceId} onValueChange={setRemixVoiceId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Choose one of your created voices" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableVoicesForRemix.map((voice) => (
                              <SelectItem key={voice.id} value={voice.id}>
                                {voice.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Only voices you've created (cloned or designed) can be remixed.
                        </p>
                      </>
                    ) : (
                      <div className="text-sm text-muted-foreground p-4 border rounded-lg bg-amber-50 border-amber-200">
                        <p className="font-medium text-amber-800 mb-1">No remixable voices yet</p>
                        <p className="text-amber-700">
                          You can only remix voices you've created. Use the "Voice Design" or "Instant Clone" tabs first to create a voice, then come back here to remix it.
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>How should the voice change?</Label>
                    <Textarea
                      value={remixDescription}
                      onChange={(event) => setRemixDescription(event.target.value)}
                      placeholder="e.g., 'Make it warmer and more conversational, with a slight Australian accent'"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sample text (optional)</Label>
                    <Textarea
                      value={remixText}
                      onChange={(event) => setRemixText(event.target.value)}
                      placeholder="Leave blank to auto-generate, or enter at least 100 characters for a custom preview text"
                    />
                    {remixText.trim() && remixText.trim().length < 100 && (
                      <p className="text-xs text-amber-600">
                        {100 - remixText.trim().length} more characters needed (minimum 100)
                      </p>
                    )}
                  </div>

                  {remixPreviews.length > 0 && (
                    <div className="space-y-3">
                      <Label>Listen to your remixed options</Label>
                      <p className="text-xs text-muted-foreground">Pick the variation that sounds best for your genie.</p>
                      <div className="grid gap-3 md:grid-cols-2">
                        {remixPreviews.map((preview) => (
                          <div key={preview.generated_voice_id} className="border rounded-lg p-3 space-y-2">
                            <audio controls src={previewAudioSrc(preview)} className="w-full" />
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={selectedRemixPreview === preview.generated_voice_id}
                                onCheckedChange={() => setSelectedRemixPreview(preview.generated_voice_id)}
                              />
                              <span className="text-sm">Use this voice</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <Label>Name your remixed voice</Label>
                        <Input value={remixName} onChange={(event) => setRemixName(event.target.value)} placeholder="e.g., 'Bella - Warmer Version'" />
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>

          <div className="sticky bottom-0 border-t px-4 sm:px-8 py-4 flex justify-start gap-3 bg-white">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button
              onClick={primaryActionConfig.onClick}
              disabled={primaryActionConfig.disabled}
              className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg"
            >
              {primaryActionConfig.isBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {primaryActionConfig.label}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default VoiceCreateModal;
